import { useCallback, useEffect, useState } from "react";

import { useNotifications } from "@/contexts/NotificationsContext";
import {
  GetAllUsersResponse,
  UpdateUserShareAccessResponse,
  UserFromApi,
} from "@/types/user";

import { useApi } from "../useApi";

/**
 * Data access for the admin "User Share Access" page.
 *
 * Reads all users from the admin-gated ``GET /admin/users`` (each carries a
 * ``shareEnabled`` flag; default-on) and toggles an individual user's share
 * access via ``PATCH /admin/users/{username}/share-access`` (optimistic,
 * rolled back on failure). Disabling adds the user to the ShareDenied Cognito
 * group; enabling removes them.
 */
const useAdminUserShareAccess = () => {
  const [rows, setRows] = useState<UserFromApi[]>([]);
  const [loading, setLoading] = useState(false);

  const { notifyError } = useNotifications();
  // isAdmin: true sends the access token the admin authorizer validates.
  const { apiGet, apiPatch } = useApi({ isAdmin: true });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiGet<GetAllUsersResponse>("/admin/users");
      setRows(
        (response?.users ?? []).map((u) => ({
          ...u,
          // Default-on: absent flag means enabled.
          shareEnabled: u.shareEnabled !== false,
        }))
      );
    } catch (error) {
      console.error(error);
      notifyError("Users Error", "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [apiGet, notifyError]);

  /** Persist a user's share access (optimistic; rolls back on failure). */
  const setShareEnabled = useCallback(
    async (username: string, enabled: boolean) => {
      setRows((prev) =>
        prev.map((r) =>
          r.username === username ? { ...r, shareEnabled: enabled } : r
        )
      );
      try {
        const response = await apiPatch<UpdateUserShareAccessResponse>(
          `/admin/users/${encodeURIComponent(username)}/share-access`,
          { enabled }
        );
        // Adopt the server's authoritative flag if it echoed one back.
        if (response && typeof response.shareEnabled === "boolean") {
          setRows((prev) =>
            prev.map((r) =>
              r.username === username
                ? { ...r, shareEnabled: response.shareEnabled }
                : r
            )
          );
        }
      } catch (error) {
        console.error(error);
        setRows((prev) =>
          prev.map((r) =>
            r.username === username ? { ...r, shareEnabled: !enabled } : r
          )
        );
        notifyError(
          "Users Error",
          `Failed to ${enabled ? "enable" : "disable"} share access`
        );
      }
    },
    [apiPatch, notifyError]
  );

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  return { rows, loading, loadUsers, setShareEnabled };
};

export default useAdminUserShareAccess;
